/**
 * Browser OpenTelemetry bootstrap.
 * Instruments fetch/XHR only for same-origin {@code /api/} and {@code /realtime-api/} URLs,
 * excluding high-volume routes via {@link shouldIgnoreUrlForBrowserTracing}.
 *
 * OTLP export is off by default. Set {@link import.meta.env.VITE_OTEL_BROWSER_EXPORT_ENABLED} to {@code true} or {@code 1} to enable export.
 *
 * Default trace sampling ratio is {@code 1} (100 %). Override with
 * {@link import.meta.env.VITE_OTEL_TRACES_SAMPLER_RATIO}; align with backend
 * {@code OTEL_TRACES_SAMPLER_ARG} when linking browser and server traces in SigNoz.
 */

import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";

import {
    buildFlowRideInstrumentedUrlMatchers,
    buildOpenTelemetryIgnoreUrlPatterns,
} from "./openTelemetryIgnoreUrls";

let initialized = false;

const readEnvBool = ( value: string | boolean | undefined, defaultValue: boolean ): boolean => {
    if ( value === undefined || value === "" ) {
        return defaultValue;
    }
    if ( typeof value === "boolean" ) {
        return value;
    }

    const normalized = value.trim().toLowerCase();

    return normalized === "true" || normalized === "1";
};

const readEnvRatio = ( value: string | undefined, defaultValue: number ): number => {
    if ( value === undefined || value.trim() === "" ) {
        return defaultValue;
    }
    const n = Number.parseFloat( value.trim() );
    if ( !Number.isFinite( n ) ) {
        return defaultValue;
    }

    return Math.min( 1, Math.max( 0, n ) );
};

/**
 * Initializes OpenTelemetry instrumentation exactly once in the browser.
 */
export const initializeOpenTelemetryBrowser = (): void => {
    if ( initialized || typeof window === "undefined" ) {
        return;
    }
    initialized = true;

    const exportEnabled = readEnvBool( import.meta.env.VITE_OTEL_BROWSER_EXPORT_ENABLED, false );
    const serviceName =  import.meta.env.VITE_OTEL_SERVICE_NAME?.trim() || "flowride-frontend";
    const ratio = readEnvRatio( import.meta.env.VITE_OTEL_TRACES_SAMPLER_RATIO, 1 );

    const tracesEndpoint =  import.meta.env.VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT?.trim();
    const defaultTracesUrl = `${ window.location.origin }/api/observability/otlp/traces`;

    const spanProcessors = [];
    if ( exportEnabled ) {
        const exporter = new OTLPTraceExporter( {
            "url":           tracesEndpoint !== undefined && tracesEndpoint !== "" ? tracesEndpoint : defaultTracesUrl,
            "timeoutMillis": 15_000,
        } );
        spanProcessors.push( new BatchSpanProcessor( exporter, {
            "maxExportBatchSize":   128,
            "scheduledDelayMillis": 5_000,
        } ) );
    }

    const provider = new WebTracerProvider( {
        "resource":       resourceFromAttributes( { "service.name": serviceName } ),
        "sampler":        new ParentBasedSampler( { "root": new TraceIdRatioBasedSampler( ratio ) } ),
        "spanProcessors": spanProcessors.length > 0 ? spanProcessors : undefined,
    } );
    provider.register( {
        "contextManager": new ZoneContextManager(),
        "propagator":     new W3CTraceContextPropagator(),
    } );

    const urlMatchers = buildFlowRideInstrumentedUrlMatchers();
    const ignoreUrls = buildOpenTelemetryIgnoreUrlPatterns();
    registerInstrumentations( {
        "instrumentations": [
            new FetchInstrumentation( {
                "propagateTraceHeaderCorsUrls": urlMatchers,
                "clearTimingResources":         true,
                ignoreUrls,
            } ),
            new XMLHttpRequestInstrumentation( {
                "propagateTraceHeaderCorsUrls": urlMatchers,
                ignoreUrls,
            } ),
        ],
    } );
};
